import { Fragment, type ReactNode } from 'react';
import { JEV_LEVEL_LABELS, jevLevelOf, needsALookLineOf, type IcpItem, type IcpItemValue, type IcpRanked, type RankingCvState } from '@nest-recruit/shared';
import { Stars } from '../../../../components/Stars';
import { candidateSummaryOf, glyphOf, glyphTitle, groupsOf, movedLabel, movedText, type CandidateFacts } from '../../icp-view';
import jevStyles from '../jev.module.css';
import styles from '../../../icp/components/icp.module.css';

// ADR 0062, mockup M2 — the columns the profile ranking draws on RNK-01: Match (level, number, stars, "fit · covers"), Per item (a box
// per line, dashed for what the person left open) and Moved (▲n / ▼n).

/** Why a person is in Needs a look, in words: no CV, a scan, or Jev gave up after three tries. */
export function needsALookLine(cv: RankingCvState): string {
  return needsALookLineOf(cv);
}

interface MatchCellProps {
  readonly rank: IcpRanked | undefined;
  /** What a person Jev has not read yet is called: "Waiting for budget" while the job's Jev budget is used up. */
  readonly waitingLabel?: string;
  /** The CV's state, to say why someone is in Needs a look. */
  readonly cv: RankingCvState;
}

export function MatchCell({ rank, cv, waitingLabel = 'Waiting for Jev' }: MatchCellProps): ReactNode {
  if (!rank) return <span className={styles.none}>—</span>;
  if (rank.state === 'unreadable') {
    return (
      <span className={jevStyles.cell}>
        <span className={jevStyles.level}>
          <span className={jevStyles.dot} data-level="none" aria-hidden />
          Needs a look
        </span>
        <span className={jevStyles.why}>{needsALookLine(cv)}</span>
      </span>
    );
  }
  if (rank.match === null) {
    return (
      <span className={jevStyles.cell}>
        <span className={jevStyles.level}>
          <span className={jevStyles.dot} data-level="none" aria-hidden />
          {waitingLabel}
        </span>
        <span className={jevStyles.why}>{waitingLabel === 'Waiting for Jev' ? 'The CV is being read' : 'Reading carries on when the budget is raised'}</span>
      </span>
    );
  }
  const level = jevLevelOf(rank.match);
  return (
    <span className={styles.matchCell}>
      <span className={jevStyles.level}>
        <span className={jevStyles.dot} data-level={level} aria-hidden />
        {JEV_LEVEL_LABELS[level]}
        <span className={jevStyles.levelNumber}>{rank.match}</span>
        <Stars value={rank.stars} />
      </span>
      <span className={styles.matchLine}>
        {rank.fit === null ? 'nothing known yet' : `fit ${rank.fit}`} · covers {rank.covers}%{rank.showsAllMusts ? '' : ' · missing a must-have'}
        {rank.state === 'not_connected' ? ' · facts only' : ''}
      </span>
    </span>
  );
}

/** The muted line under a name in the Ideal candidate order (the mockup's "3 yrs dispatch at a 3PL · Chattogram"). */
export function CandidateSummary({ facts }: { readonly facts: CandidateFacts }): ReactNode {
  const text = candidateSummaryOf(facts);
  if (text === null) return null;
  return <span className={styles.summary}>{text}</span>;
}

interface ItemGlyphsProps {
  readonly items: readonly IcpItem[];
  readonly values: readonly IcpItemValue[];
}

/** One box per line, grouped as the card is (skills · habits · English · facts), a small gap between groups. */
export function ItemGlyphs({ items, values }: ItemGlyphsProps): ReactNode {
  const valueOf = new Map(items.map((item, index) => [item.id, values[index]]));
  return (
    <span className={styles.glyphs}>
      {groupsOf(items).map((entry, groupIndex) => (
        <Fragment key={entry.group}>
          {groupIndex > 0 && <span className={styles.glyphSep} aria-hidden />}
          {entry.items.map((item) => {
            const value = valueOf.get(item.id);
            if (!value) return null;
            const glyph = glyphOf(value);
            const title = glyphTitle(item, value);
            return (
              <span key={item.id} className={styles.glyph} data-tone={glyph.tone} role="img" aria-label={title} title={title}>
                {glyph.text}
              </span>
            );
          })}
        </Fragment>
      ))}
    </span>
  );
}

export function MovedCell({ moved }: { readonly moved: number | null | undefined }): ReactNode {
  const text = movedText(moved);
  if (text === null || moved === null || moved === undefined) return <span className={styles.none}>—</span>;
  return (
    <span className={styles.moved} data-direction={moved > 0 ? 'up' : 'down'} role="img" aria-label={movedLabel(moved)} title={movedLabel(moved)}>
      {text}
    </span>
  );
}
